import React from 'react';
import { ArrowDownTrayIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface ExportModalProps {
  isOpen: boolean;
  invoiceCount: number;
  isExporting: 'csv' | 'txt' | null;
  isSubmitting?: boolean;
  onExport: (format: 'csv' | 'txt') => void;
  onSubmit?: () => void;
  onCancel: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  invoiceCount,
  isExporting,
  isSubmitting = false,
  onExport,
  onSubmit,
  onCancel,
}) => {
  if (!isOpen) return null;

  const busy = isExporting !== null || isSubmitting;

  const handleClose = () => {
    if (busy) return;
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <div className="px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <PaperAirplaneIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Submit {invoiceCount} Invoice{invoiceCount !== 1 ? 's' : ''} to Keystone
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Sends the selected invoices directly to Keystone with their PDF attached.
                A manual import file is also available as a fallback.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <button
            onClick={handleClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onExport('txt')}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExporting === 'txt' ? 'Exporting…' : 'Download import file'}
            </button>
            {onSubmit && (
              <button
                onClick={onSubmit}
                disabled={busy}
                title="Send invoice JSON + PDF directly to Keystone's inbound API"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {isSubmitting ? 'Submitting…' : 'Submit to Keystone'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
